import React from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Paper,
} from '@mui/material';
import { COLORS_ADMIN_PAGE } from '../../conf/constants';
import { Voucher } from '../types/voucher.types';
import { getDiscountTypeFromVoucherType, getVoucherTypeFromDiscountType } from '../utils/voucherUtils';

interface VoucherDetailsProps {
  voucher: Voucher;
  onFieldChange: (field: string, value: string | number) => void;
}

const VoucherDetails: React.FC<VoucherDetailsProps> = ({
  voucher,
  onFieldChange
}) => {
  const discountType = getDiscountTypeFromVoucherType(voucher.type);

  const handleDiscountTypeChange = (value: string) => {
    const voucherTypeValue = getVoucherTypeFromDiscountType(value);
    onFieldChange('type', voucherTypeValue.toString());
  };

  return (
    <Paper sx={{ p: 3, height: 'fit-content', bgcolor: COLORS_ADMIN_PAGE.WHITE, opacity: voucher.isActive ? 1 : 0.7 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          Voucher Details
        </Typography>
        <Box sx={{
          px: 2,
          py: 0.5,
          borderRadius: 1,
          bgcolor: voucher.isActive ? 'success.main' : 'error.main',
          color: 'white'
        }}>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>
            {voucher.isActive ? 'ACTIVE' : 'INACTIVE'}
          </Typography>
        </Box>
      </Box>

      {/* Voucher Code Section */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Voucher Code
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Code"
            placeholder="Code"
            disabled
            value={voucher.code}
            onChange={(e) => onFieldChange('code', e.target.value)}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Claims
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ flex: 1 }}>
              <TextField
                size="small"
                placeholder="Claimed"
                label="Claimed"
                value={voucher.claims || 0}
                fullWidth
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: COLORS_ADMIN_PAGE.LIGHT_GRAY,
                  },
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <TextField
                size="small"
                placeholder="Limit"
                label="Limit"
                value={voucher.limit || 0}
                fullWidth
                onChange={(e) => onFieldChange('limit', parseInt(e.target.value) || 0)}
                type="number"
                inputProps={{ min: 0 }}
                disabled={!voucher.isActive}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Amount and Cap Section */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Amount
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Amount"
            placeholder="Amount"
            value={voucher.amount || 0}
            onChange={(e) => onFieldChange('amount', parseInt(e.target.value) || 0)}
            type="number"
            inputProps={{ min: 0 }}
            disabled={!voucher.isActive}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle2" gutterBottom>
            Cap
          </Typography>
          <TextField
            fullWidth
            label="Cap"
            size="small"
            placeholder="Cap"
            value={voucher.cap || 0}
            onChange={(e) => onFieldChange('cap', parseInt(e.target.value) || 0)}
            type="number"
            inputProps={{ min: 0 }}
            disabled={!voucher.isActive}
          />
        </Box>
      </Box>

      {/* Discount Type Section */}
      <Box sx={{ mb: 0 }}>
        <Typography variant="subtitle2" gutterBottom>
          Discount Type
        </Typography>
        <FormControl component="fieldset">
          <RadioGroup
            value={discountType}
            onChange={(e) => handleDiscountTypeChange(e.target.value)}
          >
            <FormControlLabel
              value="additional"
              control={<Radio sx={{ color: COLORS_ADMIN_PAGE.PRIMARY }} />}
              label="Additional Credits"
              disabled={!voucher.isActive}
            />
            <FormControlLabel
              value="fixed"
              control={<Radio sx={{ color: COLORS_ADMIN_PAGE.PRIMARY }} />}
              label="Discount Fixed Amount"
              disabled={!voucher.isActive}
            />
            <FormControlLabel
              value="percentage"
              control={<Radio sx={{ color: COLORS_ADMIN_PAGE.PRIMARY }} />}
              label="Discount % up to Cap"
              disabled={!voucher.isActive}
            />
          </RadioGroup>
        </FormControl>
      </Box>
    </Paper>
  );
};

export default VoucherDetails;